import type { LocalDate } from "@js-joda/core";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { SidePanel } from "components/designV2/sidePanel/sidePanel";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { GigType } from "model/Gig";
import { useScheduleDayShifts } from "web-apps/company/components/schedule/components/hooks/useScheduleDayShifts.hook";
import { useWorkerAvatar } from "web-apps/company/components/schedule/components/hooks/useWorkerAvatar";
import { formatTimeRange } from "web-apps/company/components/schedule/utils/schedule.dates";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { formatLocalDateWeekdayLabel } from "web-apps/company/utils/timeAndDate";
import {
  DayShiftsContent,
  LoadMoreSentinel,
  LoadMoreError,
  EmptyState,
  PublicShiftAvatar,
  PublicShiftName,
  ShiftAvatar,
  ShiftAvatarImage,
  ShiftCard,
  ShiftDetails,
  ShiftName,
  ShiftRole,
  ShiftTime,
  TeamFilter,
  TeamFilters,
  TeamHeading,
  TeamSection,
  TeamShiftCount,
} from "./scheduleDayShiftsPanel.styled";
import {
  groupScheduleDayShifts,
  type DayShift,
} from "./scheduleDayShiftsPanel.logic";

interface Props {
  readonly date: LocalDate | null;
  readonly onClose: () => void;
}

interface DayShiftCardProps {
  readonly shift: DayShift;
}

const DayShiftCard = ({ shift }: DayShiftCardProps): JSX.Element => {
  const { i18n } = useTranslation();
  const isPublicOrTeamShift =
    shift.gigType === GigType.PUBLIC ||
    shift.gigType === GigType.WORKPOOL ||
    shift.gigType === GigType.WORKGROUP;
  const isUnassigned = isPublicOrTeamShift && !shift.workerName;
  const avatarUrl = useWorkerAvatar({
    workerExternalId: shift.workerExternalId,
    isWorker: shift.isWorker && !isUnassigned,
  });
  const name = shift.workerName?.trim() || "-";

  return (
    <ShiftCard>
      <ShiftTime>
        {formatTimeRange(shift.startTime, shift.endTime, i18n.language)}
      </ShiftTime>
      {isUnassigned ? (
        <PublicShiftAvatar>
          <CustomIcon
            name="v2/warning-triangle"
            size="18px"
            color={ColorV2.NegativeRed}
          />
        </PublicShiftAvatar>
      ) : (
        <ShiftAvatar>
          {avatarUrl ? (
            <ShiftAvatarImage src={avatarUrl} alt="" />
          ) : (
            <CustomIcon name="v2/person" size="18px" color={ColorV2.Blue3} />
          )}
        </ShiftAvatar>
      )}
      <ShiftDetails>
        {isUnassigned ? (
          <PublicShiftName>{name}</PublicShiftName>
        ) : (
          <ShiftName>{name}</ShiftName>
        )}
        {!isUnassigned && shift.role && <ShiftRole>{shift.role}</ShiftRole>}
      </ShiftDetails>
    </ShiftCard>
  );
};

export const ScheduleDayShiftsPanel = (props: Props): JSX.Element | null => {
  const [companyState] = useCompanyStore();
  const { i18n, t } = useTranslation();
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const { loadMore, state: dataState } = useScheduleDayShifts({
    companyId: companyState.company?.id,
    date: props.date,
  });

  useEffect(() => {
    setSelectedTeamId(null);
  }, [props.date]);

  const { loadMoreRef } = useInfiniteScroll({
    hasMore: dataState.status === "ready" && dataState.hasMore,
    isLoading:
      dataState.status === "loading" ||
      (dataState.status === "ready" && dataState.isLoadingMore),
    itemCount: dataState.status === "ready" ? dataState.data.items.length : 0,
    onLoadMore: loadMore,
  });

  const teams = useMemo(
    () =>
      dataState.status === "ready"
        ? groupScheduleDayShifts({ rows: dataState.data.items })
        : [],
    [dataState],
  );
  const visibleTeams = useMemo(
    () =>
      selectedTeamId === null
        ? teams
        : teams.filter((team) => team.id === selectedTeamId),
    [selectedTeamId, teams],
  );
  const title = props.date
    ? `${formatLocalDateWeekdayLabel(props.date, i18n.language, "short")} ${props.date.dayOfMonth()}/${props.date.monthValue()}`
    : "";

  return (
    <SidePanel
      isOpen={props.date !== null}
      onClose={props.onClose}
      title={title}
      titleId="schedule-day-shifts-title"
    >
      <DayShiftsContent
        aria-busy={
          dataState.status === "loading" ||
          (dataState.status === "ready" && dataState.isLoadingMore)
        }
      >
        {dataState.status === "ready" && teams.length > 1 && (
          <TeamFilters aria-label={t("Schedule.FilterDayShifts")}>
            <TeamFilter
              type="button"
              $isSelected={selectedTeamId === null}
              aria-pressed={selectedTeamId === null}
              onClick={() => setSelectedTeamId(null)}
            >
              {t("Schedule.All")}
            </TeamFilter>
            {teams.map((team) => (
              <TeamFilter
                key={team.id}
                type="button"
                $isSelected={selectedTeamId === team.id}
                aria-pressed={selectedTeamId === team.id}
                onClick={() => setSelectedTeamId(team.id)}
              >
                {team.name}
              </TeamFilter>
            ))}
          </TeamFilters>
        )}
        {dataState.status === "loading" ? (
          <EmptyState>{t("Schedule.LoadingDayShifts")}</EmptyState>
        ) : dataState.status === "error" ? (
          <EmptyState>{t("Schedule.LoadDayShiftsError")}</EmptyState>
        ) : visibleTeams.length === 0 ? (
          <EmptyState>{t("Schedule.NoDayShifts")}</EmptyState>
        ) : (
          visibleTeams.map((team) => (
            <TeamSection key={team.id}>
              <TeamHeading>
                {team.name}{" "}
                <TeamShiftCount>
                  {t("Schedule.ShiftCount", { count: team.shifts.length })}
                </TeamShiftCount>
              </TeamHeading>
              {team.shifts.map((shift) => (
                <DayShiftCard key={shift.id} shift={shift} />
              ))}
            </TeamSection>
          ))
        )}
        {dataState.status === "ready" && dataState.isLoadingMore && (
          <EmptyState>{t("Schedule.LoadingDayShifts")}</EmptyState>
        )}
        {dataState.status === "ready" && dataState.loadMoreError && (
          <LoadMoreError role="alert">
            <span>{t("Schedule.LoadDayShiftsError")}</span>
            <SecondaryButton
              label={t("Common.TryAgain")}
              onClick={loadMore}
              size="small"
            />
          </LoadMoreError>
        )}
        {dataState.status === "ready" && dataState.hasMore && (
          <LoadMoreSentinel ref={loadMoreRef} />
        )}
      </DayShiftsContent>
    </SidePanel>
  );
};
